import {
  computePosition,
  autoUpdate,
  arrow,
  flip,
  offset,
  inline,
  shift,
  Placement,
  OffsetOptions,
  Padding,
} from '@floating-ui/dom';

import { lazySelector, isTouchDevice } from '../../../../../resources/assets/js/utils';

export interface TooltipOptions {
  enableArrow?: boolean;
  enableTouch?: boolean;
  inline?: boolean;
  offset?: OffsetOptions;
  shift?: boolean;
  padding?: Padding;
  placement?: Placement;
  teleportTo?: HTMLElement | null;
  activateOnInit?: boolean;
  flip?: boolean;
}

const defaultOptions: TooltipOptions = {
  enableArrow: false,
  enableTouch: true,
  inline: false,
  offset: 0,
  shift: true,
  padding: 0,
  placement: 'top',
  teleportTo: null,
  activateOnInit: true,
  flip: false,
};

export const TOOLTIP_STATES = {
  active: 'phxsg-tooltip__content--active',
};

export class Tooltip {
  #arrowEl: HTMLElement | null = null;
  #cleanups = new Map();
  #el: HTMLElement;
  #initialPosition = new Map();
  #onClearEvent: EventListener;
  #onPointerEnter: EventListener;
  #onPointerLeave: EventListener;
  #onUpdatePosition: (trigger: HTMLElement, content: HTMLElement) => () => Promise<void>;
  #options: TooltipOptions;
  #teleportTarget: HTMLElement | null = null;
  #triggers: NodeListOf<HTMLElement>;

  constructor(el: HTMLElement, options: Partial<TooltipOptions> = {}) {
    this.#el = lazySelector(el);

    this.#options = {
      ...defaultOptions,
      ...options,
    };

    this.#triggers = this.#el.querySelectorAll('.phxsg-tooltip__trigger');

    if (this.#options.enableArrow) {
      this.#arrowEl = this.#el.querySelector('.phxsg-tooltip__arrow');
    }

    if (this.#options.teleportTo) {
      this.#teleportTarget = this.#options.teleportTo;
    }

    this.#onUpdatePosition = this.#updatePosition.bind(this);

    this.#onPointerEnter = (event: Event) => this.#showContent(event.target as HTMLElement);
    this.#onPointerLeave = (event: Event) => this.#hideContent(event.target as HTMLElement);

    this.#onClearEvent = this.#ignoreEvent.bind(this);

    if (!isTouchDevice() || this.#options.enableTouch) {
      this.#init();
    }
  }

  #init() {
    for (const trigger of this.#triggers) {
      const content = this.#getTriggerContent(trigger as HTMLElement);

      if (!content) {
        continue;
      }

      /**
       * When the tooltip is portaled, we need to append the content to
       * the portal element.
       */
      if (this.#teleportTarget) {
        this.#initialPosition.set(content, [content.parentNode, content.nextSibling]);

        this.#teleportTarget.appendChild(content);
      }

      if (this.#options.activateOnInit) {
        this.#activateTrigger(trigger as HTMLElement);
      }
    }
  }

  #getTriggerContent(trigger: HTMLElement) {
    const contentId = trigger.getAttribute('aria-describedby');

    return contentId && (document.querySelector(`#${contentId}`) as HTMLElement);
  }

  #activateTrigger(trigger: HTMLElement) {
    /**
     * Those events are used to toggle the tooltip state. This is useful
     * when the tooltip is used in a context where the trigger is not
     * CSS to display the tooltip.
     * the direct sibling of the tooltip content and we cannot use pure
     */
    trigger.addEventListener('pointerenter', this.#onPointerEnter);
    trigger.addEventListener('pointerleave', this.#onPointerLeave);
    trigger.addEventListener('focus', this.#onPointerEnter);
    trigger.addEventListener('blur', this.#onPointerLeave);

    trigger.setAttribute('tabindex', '0');

    /**
     * On mobile, we need to prevent events fired after the
     * initial pointerenter and before the last pointerleave events
     */
    if (isTouchDevice() && this.#options.enableTouch) {
      trigger.addEventListener('pointerdown', this.#onClearEvent);
      trigger.addEventListener('pointerup', this.#onClearEvent);
      trigger.addEventListener('click', this.#onClearEvent);
      trigger.addEventListener('contextmenu', this.#onClearEvent);
    }
  }

  #deactivateTrigger(trigger: HTMLElement) {
    this.#hideContent(trigger);

    trigger.removeEventListener('pointerenter', this.#onPointerEnter);
    trigger.removeEventListener('pointerleave', this.#onPointerLeave);
    trigger.removeEventListener('focus', this.#onPointerEnter);
    trigger.removeEventListener('blur', this.#onPointerLeave);

    trigger.removeAttribute('tabindex');

    if (isTouchDevice() && this.#options.enableTouch) {
      trigger.removeEventListener('pointerdown', this.#onClearEvent);
      trigger.removeEventListener('pointerup', this.#onClearEvent);
      trigger.removeEventListener('click', this.#onClearEvent);
      trigger.removeEventListener('contextmenu', this.#onClearEvent);
    }
  }

  #activateAllTriggers() {
    for (const trigger of this.#triggers) {
      this.#activateTrigger(trigger as HTMLElement);
    }
  }

  #deactivateAllTriggers() {
    for (const trigger of this.#triggers) {
      this.#deactivateTrigger(trigger as HTMLElement);
    }
  }

  #showContent(trigger: HTMLElement) {
    const content = this.#getTriggerContent(trigger);

    if (!content) {
      return;
    }

    this.#cleanups.set(trigger, autoUpdate(trigger, content, this.#onUpdatePosition(trigger, content)));

    content?.classList.add(TOOLTIP_STATES.active);
  }

  #hideContent(trigger: HTMLElement) {
    const content = this.#getTriggerContent(trigger);

    if (!content) {
      return;
    }

    content?.classList.remove(TOOLTIP_STATES.active);

    this.#cleanups.get(trigger)?.();
  }

  #ignoreEvent(event: Event) {
    event.preventDefault();
    event.stopPropagation();
  }

  #updatePosition(trigger: HTMLElement, content: HTMLElement) {
    return () =>
      computePosition(trigger, content, {
        placement: this.#options.placement,
        middleware: [
          this.#options.inline !== false && inline(),
          this.#options.flip !== false &&
            flip({
              fallbackPlacements: ['top-start', 'top-end'],
            }),
          this.#options.shift !== false && shift({ padding: this.#options.padding }),
          (this.#options.offset !== 0 || undefined) && offset(this.#options.offset),
          this.#arrowEl && arrow({ element: this.#arrowEl }),
        ].filter(Boolean),
      }).then(({ x, y, middlewareData, placement }) => {
        Object.assign(content.style, {
          left: `${x}px`,
          top: `${y}px`,
        });

        if (this.#arrowEl && middlewareData.arrow) {
          const { x, y } = middlewareData.arrow;

          // To simplify placement into one of this proposal [top, right, bottom, left]
          const simplePlacement = placement.split('-')[0];

          const offsetPosition = `${-this.#arrowEl.offsetWidth / 2 - 1}px`;

          const arrowPosition = {
            top: 'bottom',
            right: 'left',
            bottom: 'top',
            left: 'right',
          }[simplePlacement] as string;

          const arrowRotation = {
            top: 135,
            right: 225,
            bottom: -45,
            left: 45,
          }[simplePlacement];

          Object.assign(this.#arrowEl.style, {
            left: x !== null && arrowPosition !== 'right' ? `${x}px` : '',
            top: y !== null && arrowPosition !== 'bottom' ? `${y}px` : '',
            [arrowPosition]: offsetPosition,
            transform: `rotate(${arrowRotation}deg)`,
          });
        }
      });
  }

  destroy() {
    for (const trigger of this.#triggers) {
      const content = this.#getTriggerContent(trigger as HTMLElement);

      this.#deactivateTrigger(trigger as HTMLElement);

      if (!content) {
        continue;
      }

      if (this.#teleportTarget && this.#initialPosition.has(content)) {
        const [initialParent, nextSibling] = this.#initialPosition.get(content);

        if (nextSibling) {
          initialParent.insertBefore(content, nextSibling);
        } else {
          initialParent.appendChild(content);
        }
      }
    }
  }

  activate() {
    this.#activateAllTriggers();
  }

  deactivate() {
    this.#deactivateAllTriggers();
  }
}
